<template>
  <div class="w-full flex flex-col gap-5">
    <!-- Section Header -->
    <div class="w-full flex justify-between">
      <div class="flex flex-col gap-1">
        <BaseText type="label" size="md" class="text-text-muted">
          Data Sources
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Select the ad accounts to connect to this Lens brand.
        </BaseText>
      </div>
      <!-- Connect Data Source Dropdown -->
      <div class="relative h-8">
        <BaseTooltipV2 :tooltip-enabled="isLensGuest"
        text="Guests cannot modify data sources" offset="2">
          <button class="dropdown-opener flex items-center gap-1.5 p-1.5 rounded-md" :class="{'expanded': connectorDropdownExpanded}" 
          @click="connectorDropdownExpanded = !connectorDropdownExpanded" :disabled="loadingAdAccounts || isLensGuest">
            <BaseLoadingSpinnerCircle v-if="loadingAdAccounts" :duration="1.2" class="text-icon-normal" />
            <ConnectDataSourceIcon v-else class="text-icon-muted flex-shrink-0 fade-in" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Connect New Data Source
            </BaseText>
            <div class="transform transition-transform flex-shrink-0" :style="{ transform: connectorDropdownExpanded ? 'scaleY(-1)' : '' }">
              <ChevronIcon class="text-icon-normal" />
            </div>
          </button>
        </BaseTooltipV2>
        <transition>
          <div v-if="connectorDropdownExpanded" class="dropdown-content flex flex-col gap-1 p-1"
          v-on-clickaway="() => { connectorDropdownExpanded = false }">
            <button v-for="option in connectorPlatformOptions" :key="`connect-${option.platform}-option`"
            class="w-full flex items-center gap-2 p-1 rounded-md transition-colors duration-100 hover:bg-neutral-25 text-left"
            @click="openConnectAdAccountModal(option)">
              <div class="w-5 h-5 flex items-center justify-center">
                <component :is="option.icon" class="flex-shrink-0" :width="16" :height="16" />
              </div>
              <BaseText type="label" size="sm" class="text-text-muted flex-grow">
                {{ option.label }}
              </BaseText>
              <BaseText v-if="option.comingSoon" type="label" size="xs" class="text-text-normal px-1.5 py-1 rounded-md bg-neutral-25">
                Coming Soon
              </BaseText>
            </button>
          </div>
        </transition>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loadingAdAccounts" class="w-full flex gap-3 flex-wrap">
      <div v-for="n in numConnectedAdAccounts" :key="`ad-account-skeleton-${n}`" class="skeleton rounded-xl fade-in" style="width: 207px; height: 132px" />
    </div>
    <!-- Connected Ad Accounts -->
    <div v-else ref="connectedAdAccountsList" class="w-full flex items-start gap-3 flex-wrap">
      <!-- Accounts List -->
      <ConnectedAdAccountCard
        v-for="adAccount in connectedAdAccounts"
        :key="`connected-ad-account-${adAccount.id}-${lens.id}`"
        class="fade-in"
        :ad-account="adAccount"
        :issues-warning="getAccountIssuesWarning(adAccount)"
        :loading-remove="removingAdAccountId === adAccount.id"
        :is-lens-guest="isLensGuest"
        @remove="handleRemoveAdAccount(adAccount)"
      />
    </div>
    <!-- Repair Data Connection Section (if applicable) -->
    <div v-if="promptRepairConnection" class="w-full flex flex-col gap-3">
      <!-- Section Label -->
      <div class="w-full flex flex-col gap-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Repair Data Connection
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Complete the actions below to resolve issues with your data sources.
        </BaseText>
      </div>
      <!-- Disconnected Platform Accounts List -->
      <div v-for="provider in disconnectedProviders" :key="`disconnected-provider-${provider.id}`"
      class="w-full flex gap-3 p-3 rounded-xl border border-border-normal min-w-0">
        <div class="w-5 h-5 flex items-center justify-center">
          <component :is="getPlatformIcon(provider.platform)" class="flex-shrink-0" :width="16" :height="16" />
        </div>
        <div class="flex-grow flex flex-col gap-3">
          <!-- Label -->
          <div class="w-full flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Your {{ formatPlatformName(provider.platform) }} account disconnected
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Re-authenticate to continue pulling data for the following ad accounts:
            </BaseText>
          </div>
          <!-- Affected Ad Accounts List -->
          <div class="w-full flex items-center gap-1.5 flex-wrap">
            <div v-for="account in provider.adAccounts" :key="`account-${account.id}-platform-${provider.id}`"
            class="flex items-center gap-2 px-2 py-0.5 rounded-lg bg-neutral-25">
              <BaseText type="label" size="sm" class="text-text-muted py-0.5">
                {{ account.name }}
              </BaseText>
              <BaseText type="body" size="xs" class="text-text-subdued">
                {{ formatAccountId(account.id) }}
              </BaseText>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0">
          <button class="secondary-btn flex items-center justify-center px-2.5 py-1.5 rounded-md" style="width: 152px; height: 32px;"
          @click="handleAuthenticate(provider.platform, `pa-${provider.id}`)" :disabled="!!(loadingAuthURLId) || isLensGuest">
            <BaseLoadingSpinnerCircle v-if="loadingAuthURLId === `pa-${provider.id}`" class="text-icon-normal" :duration="1.2" />
            <BaseText v-else type="label" size="sm" class="text-text-muted whitespace-nowrap">
              Reconnect Account
            </BaseText>
          </button>
        </div>
      </div>
      <!-- Ad Accounts Missing Permissions List -->
      <div v-if="accountsMissingPermissions.length" class="w-full flex gap-3 p-3 rounded-xl border border-border-normal">
        <WarningIcon class="text-icon-normal flex-shrink-0" />
        <div class="flex-grow flex flex-col gap-3">
          <!-- Label -->
          <div class="w-full flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Missing permissions for the following ad account{{ accountsMissingPermissions.length > 1 ? 's' : '' }}
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              The ad account owner has revoked your access to {{ accountsMissingPermissions.length > 1 ? 'these accounts' : 'this account' }}. Contact the owner and re-authenticate to restore access, or remove the ad account from this Lens brand.
            </BaseText>
          </div>
          <!-- Account List -->
          <div v-for="adAccount in accountsMissingPermissions" :key="`missing-permissions-ad-account-${adAccount.id}`" class="w-full flex items-center gap-2 min-w-0">
            <!-- Loading -->
            <div v-if="loadingAdAccounts" class="flex-grow flex items-center gap-2 flex-wrap h-6">
              <div v-for="n in 3" :key="`missing-perms-skele-${n}`" class="skeleton h-5 rounded-md" :style="{width: `${140 - ((n - 1) * 30)}px`}" />
            </div>
            <div v-else class="flex-grow flex items-center gap-2 flex-wrap fade-in">
              <BaseText type="label" size="sm" class="text-text-muted py-0.5">
                {{ adAccount.name }}
              </BaseText>
              <BaseText type="label" size="xs" class="text-text-muted px-1 py-0.5 rounded-md bg-neutral-25">
                {{ adAccount.companyName }}
              </BaseText>
              <BaseText type="body" size="xs" class="text-text-subdued">
                {{ formatAccountId(adAccount.id) }}
              </BaseText>
            </div>
            <!-- Actions -->
            <div class="h-8 flex items-center gap-2 flex-shrink-0">
              <button class="flex items-center gap-1.5 px-2 pyt-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
              @click="handleAuthenticate(adAccount.platform, `mp-${adAccount.id}`)" :disabled="!!(loadingAuthURLId) || isLensGuest">
                <BaseLoadingSpinnerCircle v-if="loadingAuthURLId === `mp-${adAccount.id}`" class="text-icon-normal" :duration="1.2" />
                <EditSquareIcon v-else class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Manage
                </BaseText>
              </button>
              <button v-if="connectedAdAccounts.length > 1" class="secondary-btn flex items-center gap-1.5 p-1.5 rounded-md"
              @click="handleRemoveAdAccount(adAccount)" :disabled="!!(removingAdAccountId) || loadingAdAccounts || isLensGuest">
                <BaseLoadingSpinnerCircle v-if="removingAdAccountId === adAccount.id" class="text-icon-normal" :duration="1.2" />
                <MinusCircleIcon v-else class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Remove Ad Account
                </BaseText>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Connect Ad Account Modal -->
    <ConnectAdAccountModal 
      v-if="connectingAdAccountPlatform"
      :platform="connectingAdAccountPlatform"
      :ad-account-options="adAccounts[connectingAdAccountPlatform]"
      :existing-connections="connectedAdAccounts"
      :loading-ad-accounts="loadingAdAccounts"
      :loading-submit="loadingConnectAdAccounts"
      @connect-ad-accounts="handleConnectAdAccounts($event)"
      @refresh-ad-accounts="refreshData"
      @close="connectingAdAccountPlatform = null"
    />
    <!-- Authentication Controller Interface -->
    <LensPlatformAuthenticator 
      ref="platformAuthenticator"
      :loading="!!(loadingAuthURLId)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import { 
  formatAdAccountsByPlatform, 
  formatProvidersByPlatform, 
  enrichLensAdAccountsWithProviderData,
  providerKeyMap,
  platformIconMap,
  platformNameMap
} from '@/utils/lens/dataSourceUtils'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import LensAPI from '@/api/lens'
import moment from 'moment'
import smoothReflow from 'vue-smooth-reflow'

// Components
import ConnectedAdAccountCard from './ConnectedAdAccountCard.vue'
import ConnectAdAccountModal from './ConnectAdAccountModal.vue'

// Controllers
import LensPlatformAuthenticator from '../../lens/LensPlatformAuthenticator.vue'

// Icons
import ConnectDataSourceIcon from '../../globals/Icons/ConnectDataSourceIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import WarningIcon from '../../globals/Icons/WarningIcon.vue'
import EditSquareIcon from '../../globals/Icons/EditSquareIcon.vue'
import MinusCircleIcon from '../../globals/Icons/MinusCircleIcon.vue'

// Platform Icons
import MetaPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import LinkedInPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/LinkedInPlatformIcon.vue'

export default {
  name: 'LensAdAccountConnector',
  mixins: [clickaway, smoothReflow],
  components: {
    ConnectedAdAccountCard,
    ConnectAdAccountModal,
    LensPlatformAuthenticator,
    ConnectDataSourceIcon,
    ChevronIcon,
    WarningIcon,
    EditSquareIcon,
    MinusCircleIcon
  },
  props: {
    lens: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // Data Source States
      lensStatusAll: [],
      statusIntervalPoll: null,
      // UI States
      loadingStatus: true,
      connectorDropdownExpanded: false,
      connectorPlatformOptions: [
        { platform: 'meta', label: 'Add Meta Ad Account', icon: MetaPlatformIcon },
        { platform: 'tiktok', label: 'Add TikTok Ad Account', icon: TikTokPlatformIcon },
        { platform: 'linkedin', label: 'Add LinkedIn Ad Account', icon: LinkedInPlatformIcon, comingSoon: true }
      ],
      connectingAdAccountPlatform: null,
      loadingAuthURLId: null,
      loadingConnectAdAccounts: false,
      removingAdAccountId: null
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getProviders', 'getIsLoadingSidebarLenses']),
    numConnectedAdAccounts () {
      // Used for rendering loading skeletons
      return this.lens?.accounts?.length ?? 0
    },
    platformAccounts () {
      if (this.getIsLoadingSidebarLenses) return { meta: [], tiktok: [] }
      return formatProvidersByPlatform(this.getProviders)
    },
    adAccounts () {
      if (this.getIsLoadingSidebarLenses) return { meta: [], tiktok: [] }
      return formatAdAccountsByPlatform(this.getProviders)
    },
    connectedAdAccounts () {
      if (this.getIsLoadingSidebarLenses) return []
      const enrichedAccounts = enrichLensAdAccountsWithProviderData(this.getProviders, this.lens?.accounts ?? [])
      // Extend with data accuracy data
      const currentStatuses = this.lensStatusAll?.find(lensStatus => lensStatus.lens_id === this.lens.id)?.ad_accounts ?? []
      return enrichedAccounts.map(account => {
        const currentStatus = currentStatuses.find(accountStatus => accountStatus.ad_account_id === account.id)
        return {
          ...account,
          dataAccuracy: currentStatus?.data_accuracy ?? null,
          hasPermissions: currentStatus?.is_valid ?? true
        }
      })
    },
    accountsMissingPermissions () {
      return this.connectedAdAccounts?.filter(adAccount => !adAccount.hasPermissions) ?? []
    },
    disconnectedProviders () {
      // We use this algorithm to account for cases where an ad account is connected via multiple providers
      const providers = Object.values(this.platformAccounts).flat()
      const [connectedProviders, disconnectedProviders] = providers.reduce((acc, provider) => {
        if (provider.connected) acc[0].push(provider)
        else acc[1].push(provider)
        return acc
      }, [[], []])

      const connectedAccountIds = this.lens.accounts.map(account => account.account_id)
      const filteredDisconnections = []
      for (const accountId of connectedAccountIds) {
        // If at least one connected provider has this account, skip
        if (connectedProviders.some(provider => provider.adAccounts.some(account => account.id === accountId))) continue
        // Otherwise, find the disconnected provider that has this account
        const disconnectedProvider = disconnectedProviders.find(provider => provider.adAccounts.some(account => account.id === accountId))
        if (disconnectedProvider) filteredDisconnections.push(disconnectedProvider)
      }
      return filteredDisconnections
    },
    promptRepairConnection () {
      return this.accountsMissingPermissions.length > 0 || this.disconnectedProviders.length > 0
    },
    loadingAdAccounts () {
      return this.getIsLoadingSidebarLenses || this.loadingStatus
    },
    isLensGuest () {
      return this.lens?.accessType === 'guest'
    }
  },
  async mounted () {
    await this.fetchLensStatus()
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.connectedAdAccountsList,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    clearInterval(this.statusIntervalPoll)
    this.$unsmoothReflow({ el: this.$refs.connectedAdAccountsList })
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async fetchLensStatus () {
      this.loadingStatus = true
      try {
        const last364Days = {
          start: moment().subtract(364, 'days').startOf('day').format('YYYY-MM-DD'),
          end: moment().endOf('day').format('YYYY-MM-DD')
        }
        const { data: lensStatusAll } = await LensAPI.Lens.getAllLensStatusV2({ dateRange: last364Days })
        this.lensStatusAll = lensStatusAll

        // Check the integrity of the user's ad accounts. If any are still syncing, start a status poll
        const syncingAdAccounts = lensStatusAll?.filter(lensStatus => {
          return lensStatus?.ad_accounts?.some(accountStatus => accountStatus?.data_accuracy?.percent_diff ?? 0 > 10) ?? false
        })
        if (syncingAdAccounts.length && !this.statusIntervalPoll) {
          this.statusIntervalPoll = setInterval(() => {
            this.fetchLensStatus()
          }, 5 * 60 * 1000) // 5 minutes
        }
      } catch (error) {
        console.error('Failed to fetch lens status:', error)
      } finally {
        this.loadingStatus = false
      }
    },
    async refreshData () {
      await Promise.all([
        this.fetchLensStatus(),
        this.fetchSidebarLenses()
      ])
    },
    openConnectAdAccountModal (option) {
      if (option.comingSoon) return
      this.connectorDropdownExpanded = false
      
      // Enable the selector modal
      this.connectingAdAccountPlatform = option.platform
    },
    async handleConnectAdAccounts (adAccountIds) {
      this.loadingConnectAdAccounts = true
      try {
        const allAdAccounts = Object.values(this.adAccounts).flat()
        const accountsPayload = [ 
          ...this.lens.accounts,
          ...adAccountIds.map(accountId => {
            const connectingAccount = allAdAccounts.find(account => account.id === accountId)
            const { name, businessId, platform } = connectingAccount
            return {
              account_id: accountId,
              account_name: name,
              business_id: businessId,
              provider: providerKeyMap[platform],
              is_valid: true
            }
          })
        ]
        const updatePayload = {
          ad_accounts: accountsPayload
        }
        await LensAPI.Lens.update(this.lens.id, updatePayload)
        await this.refreshData()
        this.loadingConnectAdAccounts = false
        this.connectingAdAccountPlatform = null

        // Take action depending on the user's current route context
        if (this.$route?.params?.lensId === this.lens.id) {
          EventBus.$emit('lens:connected-ad-accounts')
        } else if (this.$route.name === 'LensView') {
          EventBus.$emit('lens:refresh-statuses')
        }

        this.$showAlert({
          type: 'success',
          message: `Ad account${adAccountIds.length > 1 ? 's' : ''} connected successfully`
        })
      } catch (error) {
        this.loadingConnectAdAccounts = false
        console.error('Failed to connect ad accounts:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to connect ad accounts. See console for details.'
        })
      }
    },
    async handleRemoveAdAccount (adAccount) {
      if (this.connectedAdAccounts.length < 2) {
        this.$showAlert({
          type: 'error',
          message: 'You must have at least one ad account connected to a Lens brand.'
        })
        return
      }

      this.removingAdAccountId = adAccount.id
      try {
        const updatePayload = { 
          ad_accounts: [...this.lens.accounts].filter(a => a.account_id !== adAccount.id)
        }
        await LensAPI.Lens.update(this.lens.id, updatePayload)
        await this.refreshData()
        this.removingAdAccountId = null

        // Take action depending on the user's current route context
        if (this.$route?.params?.lensId === this.lens.id) {
          EventBus.$emit('lens:removed-ad-account')
        } else if (this.$route.name === 'LensView') {
          EventBus.$emit('lens:refresh-statuses')
        }

        this.$showAlert({
          type: 'success',
          message: 'Ad account removed successfully'
        })
      } catch (error) {
        this.removingAdAccountId = null
        console.error('Failed to remove ad account:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to remove ad account. See console for details.'
        })
      }
    },
    async handleAuthenticate (platform, btnId) {
      this.loadingAuthURLId = btnId
      try {
        await this.$refs.platformAuthenticator.handlePlatformAuthenticate(platform)
        this.loadingAuthURLId = null
        await new Promise(resolve => setTimeout(resolve, 1000)) // Add a small delay to ensure auth was pushed to the server
        await this.refreshData()
      } catch (error) {
        this.loadingAuthURLId = null
        console.error('Failed to authenticate platform:', error)
      }
    },
    getAccountIssuesWarning (account) {
      if (this.disconnectedProviders.some(p => p.adAccounts.some(a => a.id === account.id))) {
        return `${this.formatPlatformName(account.platform)} account disconnected`
      }
      if (this.accountsMissingPermissions.some(a => a.id === account.id)) {
        return 'Missing permissions'
      }
    },
    getPlatformIcon (platform) {
      return platformIconMap[platform]
    },
    formatAccountId (id) {
      return id.replace('act_', '')
    },
    formatPlatformName (platform) {
      return platformNameMap[platform]
    }
  }
}
</script>

<style scoped>
.dropdown-opener {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.dropdown-opener:hover:not(:disabled) {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.12), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
.dropdown-opener.expanded, .dropdown-opener.expanded:hover {
  background-color: #F6F8FA;
  box-shadow: none;
}
.dropdown-opener:disabled {
  box-shadow: none;
  background-color: #F6F8FA;
  opacity: 0.5;
}
.dropdown-content {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  box-shadow: 0 24px 32px -12px rgba(53, 57, 75, 0.12), 0 1px 2px 0 rgba(162, 172, 186, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.04), 0 1px 2px 0 rgba(162, 172, 186, 0.16), 0 0 0 1px #EBEFF3;
  background-color: white;
  border-radius: 10px;
  z-index: 100;
  width: 320px;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.12), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  background-color: white;
}
.secondary-btn:hover:not(:disabled) {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.16), 0 0 0 1px rgba(0, 56, 108, 0.12);
}
.secondary-btn:disabled {
  background-color: #F6F8FA;
  box-shadow: none;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>